import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Command,
  CommandInput,
  CommandItem,
  CommandList,
  Input,
  Drawer,
  DrawerContent,
  DrawerTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
  CommandEmpty,
} from '@e2n/ui';
import { Location } from '@/domain/mitarbeiter/features/rotationsplan/components/Location';
import { ArbeitnehmerTyp } from '@/domain/mitarbeiter/features/rotationsplan/entities/ArbeitnehmerTyp';
import { Mitarbeiter } from '@/domain/mitarbeiter/features/rotationsplan/entities/Mitarbeiter';
import { ArbeitnehmerTypFilter } from '@/domain/mitarbeiter/features/rotationsplan/features/CreateRotationsplan/components/ArbeitnehmerTypFilter';
import { useMitarbeiter } from '@/domain/mitarbeiter/features/rotationsplan/hooks/useMitarbeiter/useMitarbeiter';
import { useStandortContext } from '@/features/standortContext/StandortContext';
import { cn } from '@/lib/utils';
import { faAnglesUpDown, faCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { HTMLAttributes, useState } from 'react';
import { useTranslation } from 'react-i18next';

// eslint-disable-next-line max-lines-per-function
export function MitarbeiterSelect({
  onChange,
}: {
  onChange: (mitarbeiter: Mitarbeiter) => void;
}) {
  const { t } = useTranslation();
  const { standorte } = useStandortContext();
  const standortIds = standorte.map((s) => s.id);
  const { mitarbeiter } = useMitarbeiter(standortIds);
  const [selectedMitarbeiter, setSelectedMitarbeiter] = useState<Mitarbeiter>();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [filter, setFilter] = useState('');

  const [selectedArbeitnehmerTypes, setSelectedArbeitnehmerTypes] = useState<
    ArbeitnehmerTyp[]
  >(['AZUBI']);

  function handleSelectMitarbeiter(value: string) {
    const selectedMitarbeiter = mitarbeiter.find((m) => m.id === value);
    setSelectedMitarbeiter(selectedMitarbeiter);
    if (selectedMitarbeiter) {
      onChange(selectedMitarbeiter);
    }
  }

  function handleSelectArbeitnehmerTyp(value: ArbeitnehmerTyp) {
    if (selectedArbeitnehmerTypes.includes(value)) {
      setSelectedArbeitnehmerTypes(
        selectedArbeitnehmerTypes.filter((typ) => typ !== value),
      );
    } else {
      setSelectedArbeitnehmerTypes([...selectedArbeitnehmerTypes, value]);
    }
  }

  function onChangeFilter(value: string) {
    setFilter(value);
  }

  const filteredMitarbeiter = mitarbeiter.filter(
    (m) =>
      m.arbeitnehmerTyp !== undefined &&
      selectedArbeitnehmerTypes.includes(m.arbeitnehmerTyp),
  );

  const filteredMitarbeiterDrawer = filteredMitarbeiter.filter((m) =>
    m.getName().toLocaleLowerCase().includes(filter.toLocaleLowerCase()),
  );

  return (
    <div>
      <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen} modal>
        <PopoverTrigger className="hidden md:flex" asChild>
          <Trigger selectedMitarbeiter={selectedMitarbeiter} />
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className={`bg-white p-2 px-0 w-108 overflow-auto`}>
          <Command>
            <div className="flex items-center justify-between">
              <div className="w-full">
                <CommandInput
                  placeholder={t(
                    'domain.mitarbeiter.rotationsplan.filter.suchen',
                  )}
                />
              </div>
              <ArbeitnehmerTypFilter
                selectedArbeitnehmerTypes={selectedArbeitnehmerTypes}
                handleSelectArbeitnehmerTyp={handleSelectArbeitnehmerTyp}
              />
            </div>
            <CommandList>
              <CommandEmpty className="text-neutrals-700 py-1">
                {t('domain.mitarbeiter.rotationsplan.filter.keineErgebnisse')}
              </CommandEmpty>
              {filteredMitarbeiter.map((mitarbeiter) => (
                <CommandItem
                  key={mitarbeiter.id}
                  value={mitarbeiter.getName() + '%' + mitarbeiter.id}
                  onSelect={() => {
                    handleSelectMitarbeiter(mitarbeiter.id);
                    setIsPopoverOpen(false);
                  }}>
                  <ListItem
                    mitarbeiter={mitarbeiter}
                    selectedMitarbeiter={selectedMitarbeiter}
                  />
                </CommandItem>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <Drawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
        <DrawerTrigger className="flex md:hidden" asChild>
          <Trigger selectedMitarbeiter={selectedMitarbeiter} />
        </DrawerTrigger>
        <DrawerContent className="flex flex-col gap-4 p-4">
          <div className="font-semibold text-center">
            {t(
              'domain.mitarbeiter.rotationsplan.rotationsplaene.createRotationsplan.fields.mitarbeiter.label',
            )}
          </div>
          <div className="flex items-center justify-between">
            <Input
              className="grow"
              defaultValue={filter}
              placeholder={t('domain.mitarbeiter.rotationsplan.filter.suchen')}
              onChange={(event) => onChangeFilter(event.target.value)}
            />
            <ArbeitnehmerTypFilter
              handleSelectArbeitnehmerTyp={handleSelectArbeitnehmerTyp}
              selectedArbeitnehmerTypes={selectedArbeitnehmerTypes}
            />
          </div>
          <ScrollArea className="overflow-auto">
            <div className="flex flex-col gap-2">
              {!filteredMitarbeiterDrawer.length && (
                <div className="text-neutrals-700 py-1 text-center text-sm">
                  {t('domain.mitarbeiter.rotationsplan.filter.keineErgebnisse')}
                </div>
              )}
              {filteredMitarbeiterDrawer.map((m, index) => (
                <div
                  className="flex flex-col gap-2"
                  key={m.id}
                  onClick={() => {
                    handleSelectMitarbeiter(m.id);
                    setIsDrawerOpen(false);
                  }}>
                  <ListItem
                    mitarbeiter={m}
                    selectedMitarbeiter={selectedMitarbeiter}
                  />
                  {index < filteredMitarbeiterDrawer.length - 1 && (
                    <div className="w-full bg-neutrals-200 h-px"></div>
                  )}
                </div>
              ))}
            </div>
          </ScrollArea>
        </DrawerContent>
      </Drawer>
    </div>
  );
}

function Trigger({
  selectedMitarbeiter,
  ...props
}: {
  selectedMitarbeiter?: Mitarbeiter;
} & HTMLAttributes<HTMLButtonElement>) {
  const { t } = useTranslation();
  return (
    <Button
      {...props}
      variant="outline-secondary"
      role="combobox"
      className={cn(
        'w-full justify-between font-normal  [&>svg]:text-neutrals-900',
        selectedMitarbeiter ? 'text-neutrals-900' : 'text-neutrals-700',
        props.className,
      )}>
      {selectedMitarbeiter
        ? selectedMitarbeiter.getName()
        : t('actions.auswaehlen')}
      <FontAwesomeIcon icon={faAnglesUpDown} />
    </Button>
  );
}

function ListItem({
  mitarbeiter,
  selectedMitarbeiter,
}: {
  mitarbeiter: Mitarbeiter;
  selectedMitarbeiter?: Mitarbeiter;
}) {
  return (
    <div className="flex w-full items-center gap-2">
      <Avatar>
        <AvatarImage
          alt={mitarbeiter.vorname + ' ' + mitarbeiter.nachname}
          src={mitarbeiter.imgUrl}
        />
        <AvatarFallback>{mitarbeiter.getInitials()}</AvatarFallback>
      </Avatar>
      <div className="flex items-center justify-between w-full">
        <div className="min-w-0 font-normal">
          <div className="whitespace-nowrap truncate text-sm">
            {mitarbeiter.getName()}
          </div>
          <Location standort={mitarbeiter.standort} />
        </div>
        {mitarbeiter.id === selectedMitarbeiter?.id && (
          <FontAwesomeIcon className="text-primary-blue-600" icon={faCheck} />
        )}
      </div>
    </div>
  );
}
